import React from "react";
import { Edit } from "@mui/icons-material";
import { Alert, AlertTitle, Button, Paper } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import useWorkflowErrorLogs from "fond/hooks/useWorkflowErrorLogs";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  analyze: { label: "Analyze" },
  design: { label: "Design" },
  refine: { label: "Refine" },
};

interface DesignPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
  isUploading?: boolean;
}

const DesignPanel: React.FC<DesignPanelProps> = ({ isReadOnly, canRun, isUploading }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { open } = useStackedNavigationContext();
  const { workflowState, stages, runWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.ISG.SOLVE,
  });
  const { errorLogsInline, logsModal } = useWorkflowErrorLogs(logs);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleRunSolve = () => {
    runWorkflow?.();
  };

  const handleDrawBlockers = () => {
    open("design-blockers-draw");
  };

  const handleDrawHubs = () => {
    open("design-hubs");
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload boundary and address layers before running design generation.
        </Alert>
      )}

      {canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Design generation is ready to start.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Design generation failed</AlertTitle>
          There was an error running design generation. Please try again.
          {errorLogsInline}
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Design generation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isReadOnly && (
        <>
          {!isInProgress && (
            <StackedNavigationButton icon={<Edit />} title="Draw blockers" onClick={handleDrawBlockers} sx={{ m: 0, mt: 2 }} disabled={isUploading} />
          )}
          {workflowState === "COMPLETE" && (
            <StackedNavigationButton icon={<Edit />} title="Modify hubs" onClick={handleDrawHubs} sx={{ m: 0, mt: 2 }} disabled={isUploading} />
          )}
        </>
      )}

      {!isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ my: 2 }}
          disabled={!runWorkflow || !canRun || isInProgress || isReadOnly || isUploading}
          onClick={handleRunSolve}
        >
          {workflowState === "COMPLETE" ? "Re-run design generation" : "Start design generation"}
        </Button>
      )}

      {isUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      {logsModal}
    </>
  );
};

export default DesignPanel;
